import { CloseOutlined } from '@ant-design/icons';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Button, DatePicker, Form, InputNumber, notification, Select, Skeleton, Switch } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import React, { ChangeEvent, DragEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { config } from '../../config';
import CustomInput from '../CustomComponents/CustomInput';
import { GET_PARTICIPANTS } from '../Party/PartyQueries';
import { additionalOptions, typeOptions, UPDATE_DATA_FILE } from './DataFilesConstants';

type UploadFileProps = {
  closeModal: () => void;
  fileDetails: any;
  mainExhibits: number[];
};
const UploadFile: React.FC<UploadFileProps> = ({
  closeModal,
  fileDetails,
  mainExhibits,
}: UploadFileProps) => {
  const today = new Date();
  const [files, setFiles] = useState<File[]>([]);
  const dropRef = useRef<HTMLDivElement>(null);
  const { proceedingId } = useParams<any>();
  const [form] = Form.useForm();
  const [isConfidential, setIsConfidential] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [showOtherSubmitterTextBox, setShowOtherSubmitterTextBox] = useState(false);
  const [isApplicationMaterials, setIsApplicationMaterials] = useState(false);
  const [isACOReport, setisACOReport] = useState(false);
  const [isIRDates, setisAIRDates] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [isOverride, setIsOverride] = useState(false)

  const [mainExhibitOptions, setMainExhibitOptions] = useState([]);
  const buttonlabel = fileDetails ? 'Update Details' : 'Upload';
  const handleConfidentialChange = (checked: boolean) => {
    setIsConfidential(checked);
  };
  const initialValues = {
    type: '',
    dateMaterialRequested: '',
    dateMaterialReceivedFromBranch: '',
    dateMaterialsSentToCompanyForValidation: '',
    dateCompanyCompletedValidation: '',
    dateEvidentiaryPortionOfHearingClosed: '',
    IRStartDate: '',
    IREndDate: '',
    comments: '',
    exhibitMajor: '',
    securityClassification: '',
    participantId: '',
    updateData: '',
    otherSubmitter: '',
  };
  const [updateFileData, { loading: updating, error: updateError, data: updateData }] =
    useMutation(UPDATE_DATA_FILE);

  const [getParticipants, { data: response, loading, error, refetch }] =
    useLazyQuery(GET_PARTICIPANTS);

  useEffect(() => {
    if (proceedingId) {
      getParticipants({
        variables: {
          input: { proceedingNumber: proceedingId },
        },
      });
    }

    setIsApplicationMaterials(fileDetails?.type?.includes('Application Materials') ? true : false);
    setisACOReport(fileDetails?.type?.includes('ACO - Report') ? true : false);
    setisAIRDates(
      fileDetails?.type?.includes('Information Request, Response, etc.') ? true : false
    );
    if (fileDetails) {
      form.setFieldsValue({
        ...fileDetails,
        participantId:
          fileDetails.otherSubmitter === 'Hearing Panel'
            ? 'Hearing Panel'
            : fileDetails.otherSubmitter
            ? 'Other'
            : fileDetails.participantId,
        otherSubmitter:
          fileDetails.otherSubmitter !== 'Hearing Panel' ? fileDetails.otherSubmitter : '',
      });
      if (fileDetails.actualRecievedOrSentDate) {
        form.setFieldValue('actualRecievedOrSentDate', dayjs(fileDetails.actualRecievedOrSentDate));
      }
      if (fileDetails.dateMaterialRequested) {
        form.setFieldValue('dateMaterialRequested', dayjs(fileDetails.dateMaterialRequested));
      }
      if (fileDetails.dateMaterialReceivedFromBranch) {
        form.setFieldValue(
          'dateMaterialReceivedFromBranch',
          dayjs(fileDetails.dateMaterialReceivedFromBranch)
        );
      }
      if (fileDetails.dateMaterialsSentToCompanyForValidation) {
        form.setFieldValue(
          'dateMaterialsSentToCompanyForValidation',
          dayjs(fileDetails.dateMaterialsSentToCompanyForValidation)
        );
      }
      if (fileDetails.dateCompanyCompletedValidation) {
        form.setFieldValue(
          'dateCompanyCompletedValidation',
          dayjs(fileDetails.dateCompanyCompletedValidation)
        );
      }
      if (fileDetails.dateEvidentiaryPortionOfHearingClosed) {
        form.setFieldValue(
          'dateEvidentiaryPortionOfHearingClosed',
          dayjs(fileDetails.dateEvidentiaryPortionOfHearingClosed)
        );
      }
      if (fileDetails.IRStartDate) {
        form.setFieldValue('IRStartDate', dayjs(fileDetails.IRStartDate));
      }
      if (fileDetails.IREndDate) {
        form.setFieldValue('IREndDate', dayjs(fileDetails.IREndDate));
      }

      setShowOtherSubmitterTextBox(
        !!fileDetails.otherSubmitter && fileDetails.otherSubmitter !== 'Hearing Panel'
      );
      setIsConfidential(fileDetails.securityClassification === 'Confidential');
    } else {
      form.setFieldsValue(initialValues);
      form.setFieldValue('type', []);
    }
  }, [fileDetails]);

  const participants =
    response?.participants?.data.map((participant: any) => ({
      value: participant.id,
      label: `${participant.partyName}`,
    })) || [];

  useEffect(() => {
    const arr: { label: number; value: number }[] = [];
    mainExhibits.forEach((exhibit) => arr.push({ value: exhibit, label: exhibit }));
    setMainExhibitOptions(arr);
  }, [mainExhibits]);

  const allParticipantOptions = [...(participants || []), ...additionalOptions];

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) {
      dropRef.current.classList.add('dragging');
    }
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) {
      dropRef.current.classList.remove('dragging');
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (dropRef.current) {
      dropRef.current.classList.remove('dragging');
    }

    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prevFiles) => [...prevFiles, ...droppedFiles]);
  };

  const handleFileInput = (e: ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files ? Array.from(e.target.files) : [];
    setFiles((prevFiles) => [...prevFiles, ...selectedFiles]);
  };

  const uploadFiles = async (values: any) => {
    if(fileDetails){
      await updateDataFiles()
    } else {
      if(isOverride && files.length !==1){
        notification.error({ message: 'Only 1 file allowed while enterring Exhibit Numbers manually' });
        return;
      }
      if (files.length === 0) {
        notification.error({ message: 'Add at least 1 file!' });
        return;
      }
      if (files.length > 5) {
        notification.error({ message: 'Max 5 files are allowed at a time' });
        return;
      }
  
      setIsUploading(true);
      const formData = new FormData();
      files.forEach((file) => {
        formData.append('files', file);
      });
      formData.append('proceedingId', proceedingId);
  
      if (values.participantId === 'Other' && values.otherSubmitter) {
        formData.append('otherSubmitter', values.otherSubmitter);
      } else if (values.participantId === 'Hearing Panel') {
        formData.append('otherSubmitter', 'Hearing Panel');
      } else {
        formData.append('participantId', values.participantId);
      }
  
      if (values.comments) {
        formData.append('comments', values.comments);
      }
      if(isOverride){
        formData.append('overrideExhibiMajor', values.overrideExhibiMajor);
        formData.append('overrideExhibiMinor', values.overrideExhibiMinor);
      }else if (values.exhibitMajor) {
        formData.append('exhibitMajor', values.exhibitMajor);
      }
      if (values.actualRecievedOrSentDate) {
        formData.append(
          'actualRecievedOrSentDate',
          dayjs(values.actualRecievedOrSentDate).toString()
        );
      }
      if (values.dateMaterialRequested) {
        formData.append('dateMaterialRequested', dayjs(values.dateMaterialRequested).toString());
      }
      if (values.dateMaterialReceivedFromBranch) {
        formData.append(
          'dateMaterialReceivedFromBranch',
          dayjs(values.dateMaterialReceivedFromBranch).toString()
        );
      }
      if (values.dateMaterialsSentToCompanyForValidation) {
        formData.append(
          'dateMaterialsSentToCompanyForValidation',
          dayjs(values.dateMaterialsSentToCompanyForValidation).toString()
        );
      }
      if (values.dateCompanyCompletedValidation) {
        formData.append(
          'dateCompanyCompletedValidation',
          dayjs(values.dateCompanyCompletedValidation).toString()
        );
      }
      if (values.dateEvidentiaryPortionOfHearingClosed) {
        formData.append(
          'dateEvidentiaryPortionOfHearingClosed',
          dayjs(values.dateEvidentiaryPortionOfHearingClosed).toString()
        );
      }
      if (values.IRStartDate) {
        formData.append('IRStartDate', dayjs(values.IRStartDate).toString());
      }
      if (values.IREndDate) {
        formData.append('IREndDate', dayjs(values.IREndDate).toString());
      }
      if (isConfidential) {
        formData.append('securityClassification', 'Confidential');
      } else {
        formData.append('securityClassification', 'Public');
      }
      selectedTypes.forEach((type) => {
        formData.append('type[]', type);
      });
      try {
        await axios.post(`${config.REACT_APP_SERVICE_URI}/file/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: localStorage.getItem('accessToken'),
          },
        });
        notification.success({ message: 'File uploaded' });
        form.resetFields();
        setIsUploading(false);
        setFiles([]);
        closeModal();
      } catch (error) {
        const backendErrorMessage = error.response?.data?.message || 'Unexpected error.';
        setIsUploading(false);
        notification.error({
          message: backendErrorMessage
        });
      }
    }
    
  };

  const updateDataFiles = async () => {
    if (!form.getFieldValue('participantId')) {
      notification.error({ message: `Submitter is mandatory for ${form.getFieldValue('type')}` });
      return;
    }
    await updateFileData({
      variables: {
        input: {
          comments: form.getFieldValue('comments'),
          id: fileDetails.id,
          participantId:
            form.getFieldValue('participantId') === 'Other'
              ? null
              : form.getFieldValue('participantId'),
          otherSubmitter:
            form.getFieldValue('participantId') === 'Other'
              ? form.getFieldValue('otherSubmitter')
              : form.getFieldValue('participantId') === 'Hearing Panel'
              ? 'Hearing Panel'
              : null,

          securityClassification: isConfidential ? 'Confidential' : 'Public',
          type: form.getFieldValue('type'),
          proceedingNumber: proceedingId,
          actualRecievedOrSentDate: form.getFieldValue('actualRecievedOrSentDate'),
          dateMaterialRequested: isApplicationMaterials
            ? form.getFieldValue('dateMaterialRequested')
            : '',
          dateMaterialReceivedFromBranch: isApplicationMaterials
            ? form.getFieldValue('dateMaterialReceivedFromBranch')
            : '',
          dateMaterialsSentToCompanyForValidation: isApplicationMaterials
            ? form.getFieldValue('dateMaterialsSentToCompanyForValidation')
            : '',
          dateCompanyCompletedValidation: isApplicationMaterials
            ? form.getFieldValue('dateCompanyCompletedValidation')
            : '',
          dateEvidentiaryPortionOfHearingClosed: isACOReport
            ? form.getFieldValue('dateEvidentiaryPortionOfHearingClosed')
            : '',
          IRStartDate: isIRDates ? form.getFieldValue('IRStartDate') : '',
          IREndDate: isIRDates ? form.getFieldValue('IREndDate') : '',
        },
      },
    });
  };

  useEffect(() => {
    if (updateData || updateError) {
      if (updateData && updateData.editDataFile.result === 'Success') {
        notification.success({ message: 'Details Updated' });
        form.resetFields();
        setIsUploading(false);
        setFiles([]);
        closeModal();
      } else {
        notification.error({ message: 'Error while updating data file' });
        setIsUploading(false);
      }
    }
  }, [updateData, updateError]);

  const handleTypeSelectionChange = (values: string[]) => {
    setSelectedTypes(values);
    setIsApplicationMaterials(values.includes('Application Materials') ? true : false);
    setisACOReport(values.includes('ACO - Report') ? true : false);
    setisAIRDates(values.includes('Information Request, Response, etc.') ? true : false);
  };

  return (
    <div>
      {updating || isUploading ? (
        <Skeleton />
      ) : (
        <div className="thin-border">
          <Form form={form} layout="vertical" onFinish={uploadFiles}>
            <Form.Item
              className="type-of-regulatory-event"
              name="type"
              label="Type"
              required
              rules={[{ required: true, message: 'Please select the type' }]}
            >
              <Select
                mode="multiple"
                allowClear
                placeholder="Select types"
                options={typeOptions}
                onChange={handleTypeSelectionChange}
              />
            </Form.Item>
            {isApplicationMaterials && (
              <>
                <Form.Item name="dateMaterialRequested" label="Date Material Requested">
                  <DatePicker
                    style={{ width: '100%' }}
                    disabledDate={(current) => current.isAfter(today)}
                  />
                </Form.Item>
                <Form.Item
                  name="dateMaterialReceivedFromBranch"
                  label="Date Material Received From Branch"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    disabledDate={(current) => current.isAfter(today)}
                  />
                </Form.Item>
                <Form.Item
                  name="dateMaterialsSentToCompanyForValidation"
                  label="Date Materials Sent To Company For Validation"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    disabledDate={(current) => current.isAfter(today)}
                  />
                </Form.Item>
                <Form.Item
                  name="dateCompanyCompletedValidation"
                  label="Date Company Completed Validation"
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    disabledDate={(current) => current.isAfter(today)}
                  />
                </Form.Item>
              </>
            )}
            {isACOReport && (
              <Form.Item
                name="dateEvidentiaryPortionOfHearingClosed"
                label="Date Evidentiary Portion Of Hearing Closed"
              >
                <DatePicker
                  style={{ width: '100%' }}
                  disabledDate={(current) => current.isAfter(today)}
                />
              </Form.Item>
            )}
            {isIRDates && (
              <>
                <Form.Item name="IRStartDate" label="IR Start Date">
                  <DatePicker
                    style={{ width: '100%' }}
                    disabledDate={(current) => current.isAfter(today)}
                  />
                </Form.Item>
                <Form.Item name="IREndDate" label="IR End Date">
                  <DatePicker
                    style={{ width: '100%' }}
                    disabledDate={(current) => current.isAfter(today)}
                  />
                </Form.Item>
              </>
            )}
            <Form.Item
              className="type-of-regulatory-event"
              name="participantId"
              label="Submitter"
              rules={[{ required: true, message: 'Either Submitter or Other must be selected' }]}
            >
              <Select
                options={allParticipantOptions}                
                onChange={(value) => {
                  if (value === 'Other') {
                    setShowOtherSubmitterTextBox(true);
                  } else {
                    setShowOtherSubmitterTextBox(false);
                  }
                }}
              />
            </Form.Item>
            {showOtherSubmitterTextBox && (
              <Form.Item
                name="otherSubmitter"
                label="Other Submitter"
                rules={[{ required: true, message: 'Please specify Other Submitter' }]}
              >
                <CustomInput showInput={true} />
              </Form.Item>
            )}
            {!fileDetails && (
            <Form.Item name="isOverride" label="Override Exhibit number"
            >
              <Switch checked={isOverride} onChange={setIsOverride} />
            </Form.Item>
          )}

          {!fileDetails && isOverride && (
            <Form.Item name="overrideExhibiMajor" label="Main Exhibit number"
            rules={[{ required: true, message: 'Please Enter Number' }]}
            >
              <InputNumber min={0}/>
            </Form.Item>
          )}
          {!fileDetails && isOverride && (
            <Form.Item name="overrideExhibiMinor" label="Sub Exhibit number"
            rules={[{ required: true, message: 'Please Enter Number' }]}
            >
              <InputNumber min={0}/>
            </Form.Item>
          )}
          {!fileDetails && !isOverride && (
            <Form.Item name="exhibitMajor" label="Main Exhibit number">
              <Select options={mainExhibitOptions} allowClear />
            </Form.Item>
          )}

            <Form.Item
              name="actualRecievedOrSentDate"
              label="Date Received/Sent"
              rules={[{ required: true, message: 'Please select the Received/Sent date' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                disabledDate={(current) => current.isAfter(today)}
                data-testid="result-date-picker"
              />
            </Form.Item>

            <Form.Item
              className="type-of-regulatory-event"
              name="securityClassification"
              label="Confidential"
            >
              <Switch checked={isConfidential} onChange={handleConfidentialChange} />
            </Form.Item>
            <Form.Item name="comments" label="Comments">
              <CustomInput showInput={true} />
            </Form.Item>
            {!fileDetails && (
              <>
                <div
                  ref={dropRef}
                  className="dropzone"
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  Drag and drop files here or click to select files
                </div>
                <input
                  type="file"
                  multiple
                  onChange={handleFileInput}
                  style={{ display: 'none' }}
                  id="fileInput"
                />
                <label htmlFor="fileInput" className="file-input-label">
                  Select File
                </label>
                <div className="file-list">
                  {files?.map((file, index) => (
                    <div key={index} className="file-item">
                      {file.name}
                      <Button
                        onClick={() => setFiles(files.filter((f) => f.name !== file.name))}
                        icon={<CloseOutlined />}
                        size={'small'}
                        style={{ backgroundColor: 'white', color: 'red' }}
                      />
                    </div>
                  ))}
                </div>
                <Button type="primary" htmlType="submit" disabled={isUploading}>
                  {isUploading ? 'Uploading' : buttonlabel}
                </Button>
              </>
            )}
            {fileDetails && (
              <Button type="primary" htmlType="submit">
                {isUploading ? 'Updating' : buttonlabel}
              </Button>
            )}
            <Button
              onClick={() => {
                form.setFieldsValue(initialValues);
                closeModal();
              }}
            >
              Cancel
            </Button>
          </Form>
        </div>
      )}
    </div>
  );
};

export default UploadFile;
